import { Badge } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { getSeverityMeta } from '../utils/severityUtils.js';
import { humanizeStatus } from '../utils/humanizeStatus.js';
import { shortEventId } from '../utils/formatEventId.js';
import OwnerChip from './Team/OwnerChip.jsx';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

/**
 * Detail header: optional back, then the title, with the identity chips and
 * status underneath — the prototype leads with the title. No boxed chrome; the
 * split view renders this above the detail card, not inside it.
 */
function EventDetailHeader({ event, onBack, showBack = true }) {
  const { t } = useTranslation('work-queues-app');
  const severityMeta = getSeverityMeta(event.severity);

  return (
    <div className="flex flex-col gap-2.5">
      {showBack && onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex w-fit items-center gap-1.5 text-sm text-content-muted transition-colors hover:text-content"
        >
          <ArrowLeft size={16} />
          {t('detail.backToQueue')}
        </button>
      ) : null}

      <h1 className="text-xl font-semibold leading-snug text-content">{event.title}</h1>

      <div className="flex flex-wrap items-center gap-1.5">
        <Badge variant={severityMeta.tone === 'coral' ? 'error' : 'informative'} size="small">
          {t(severityMeta.labelKey)}
        </Badge>
        {event.category && (
          <Badge variant="purple" size="small">
            {event.category}
          </Badge>
        )}
        {event.isCustomer && (
          <Badge variant="purple" size="small">
            {t('card.customer')}
          </Badge>
        )}
        {/* Short form only (CT-5308) — the full UUID used to be printed next to
            this chip. `refId` is null for plenty of rows (any API event without
            ref_id, a HITL whose short-ref chain resolves to nothing, most
            mocks), so it falls back to the same shortened id the inbox row and
            the trace slideout show, with the full id in the tooltip. */}
        <span className="font-mono text-xs text-content-faint" title={event.id}>
          {event.refId ?? shortEventId(event.id)}
        </span>
        {event.refLabel && (
          <Badge variant="neutral" size="small">
            {event.refLabel}
          </Badge>
        )}
        {event.actionLabel && (
          <Badge variant="informative" size="small">
            {event.actionLabel}
          </Badge>
        )}
        {event.delegatedFrom && (
          <Badge variant="warning" size="small">
            {t('card.coveringFor', { name: event.delegatedFrom })}
          </Badge>
        )}
        {event.missionCritical && (
          <Badge variant="error" size="small" icon={AlertTriangle}>
            {t('card.missionCritical')}
          </Badge>
        )}
        <span className="ml-auto flex items-center gap-2">
          {event.dueLabel && (
            <span className={['text-sm', event.overdue ? 'text-rose-500' : 'text-content-muted'].join(' ')}>
              {event.dueLabel}
            </span>
          )}
          <Badge variant={event.status === 'open' ? 'success' : 'classicPrimary'} size="small">
            {t(`status.${event.status}`, { defaultValue: humanizeStatus(event.status) })}
          </Badge>
        </span>
      </div>

      {event.owner !== undefined ? <OwnerChip owner={event.owner} /> : null}
    </div>
  );
}

export default EventDetailHeader;
